import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LegalBackToLogin from "../components/LegalBackToLogin";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    background: themeColors.bg,
    color: themeColors.text,
    padding: "48px 18px",
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 28,
    boxShadow: themeColors.glow,
    padding: "34px clamp(20px, 4vw, 48px)",
    backdropFilter: "blur(18px)",
  },
  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 10,
  },
  title: {
    fontSize: "clamp(32px, 5vw, 52px)",
    lineHeight: 1.02,
    margin: "0 0 10px",
    color: themeColors.text,
  },
  date: {
    color: themeColors.mutedText,
    marginBottom: 28,
    fontSize: 15,
  },
  section: {
    marginTop: 28,
    paddingTop: 22,
    borderTop: `1px solid ${themeColors.border}`,
  },
  h2: {
    color: themeColors.accent,
    fontSize: 22,
    margin: "0 0 10px",
  },
  p: {
    color: themeColors.text,
    fontSize: 16,
    lineHeight: 1.75,
    margin: "8px 0",
  },
  list: {
    color: themeColors.text,
    lineHeight: 1.75,
    paddingLeft: 22,
  },
  nav: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 36,
    paddingTop: 24,
    borderTop: `1px solid ${themeColors.border}`,
  },
  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 800,
  },
  footer: {
    marginTop: 22,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },
});

export default function TermsAndConditions() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  return (
    <main style={styles.page}>
      <article style={styles.card}>
        <div style={styles.eyebrow}>AVAAIO Legal</div>
        <h1 style={styles.title}>Terms & Conditions</h1>
        <div style={styles.date}>
          Effective and Last Updated: July 13, 2026
        </div>

        <section style={styles.section}>
          <h2 style={styles.h2}>Agreement</h2>
          <p style={styles.p}>
            These Terms & Conditions govern access to AVAAIO™, Ava AI,
            our websites, applications, accounts, subscriptions, and
            related services operated by American Globalo, LLC.
          </p>
          <p style={styles.p}>
            By creating an account, accessing AVAAIO, or using any
            feature, the user agrees to these Terms, the Privacy
            Policy, and the AI Disclaimer. A user who does not agree
            must not use AVAAIO.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Eligibility and Minors</h2>
          <p style={styles.p}>
            AVAAIO is not directed to children under 13. A person under
            13 may not create or independently use an AVAAIO account.
            Users who have not reached the age of majority may use the
            service only with permission and supervision from a parent
            or legal guardian.
          </p>
          <p style={styles.p}>
            A parent or guardian who permits a minor to use AVAAIO is
            responsible for the minor’s activity, submitted content,
            purchases, permissions, and compliance with these Terms.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Accounts and Registration</h2>
          <p style={styles.p}>
            Users must provide accurate account information, maintain
            control of their login credentials, and promptly report
            suspected unauthorized access. Accounts may not be sold,
            transferred, shared for abusive purposes, or created using
            false or unauthorized information.
          </p>
          <p style={styles.p}>
            Public registration may be temporarily closed, limited,
            invite-only, or opened in stages while AVAAIO prepares for
            early access or public release. American Globalo, LLC does
            not guarantee that registration, preview access, or any
            particular feature will be available at a specific time.
          </p>
          <p style={styles.p}>
            Email verification, account approval, plan validation,
            additional administrator authentication, or other
            security checks may be required before access is granted.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Free, Preview, and Paid Access</h2>
          <p style={styles.p}>
            AVAAIO may offer Free Standard access, limited previews,
            early-access features, paid subscriptions, lifetime
            access, promotional access, or account-specific
            entitlements.
          </p>
          <p style={styles.p}>
            A preview or early-access period does not create a paid
            subscription, authorize automatic billing, guarantee
            continued premium access, or require the user to submit a
            rating or review. At the end of a preview, the account may
            return to the Free Standard feature set.
          </p>
          <p style={styles.p}>
            Paid checkout and paid-plan features may remain
            unavailable until AVAAIO’s public release or until the
            applicable billing system is fully enabled. Features
            displayed in the interface may be locked, under review,
            experimental, unavailable, or subject to additional
            eligibility requirements.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Subscriptions and Billing</h2>
          <p style={styles.p}>
            When paid subscriptions become available, purchases may
            be processed by Apple, Stripe, or another authorized
            billing provider, depending on the platform and purchase
            method.
          </p>
          <p style={styles.p}>
            The price, billing period, included features, renewal
            terms, taxes, and cancellation instructions presented at
            checkout are part of these Terms. Recurring subscriptions
            may renew automatically until canceled through the
            applicable billing provider or account-management method.
          </p>
          <p style={styles.p}>
            Apple purchases are managed through the user’s Apple ID
            and are also subject to Apple’s terms and refund process.
            Stripe or web purchases may be subject to Stripe’s
            processing terms and the cancellation method presented by
            AVAAIO.
          </p>
          <p style={styles.p}>
            Except where required by law or provider policy, fees are
            generally non-refundable. Canceling a subscription
            normally stops future renewal and does not necessarily
            end access before the current paid period expires.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Camera, Microphone, and Motion Permissions</h2>
          <p style={styles.p}>
            Camera, microphone, and device-orientation features require
            user activation and may require operating-system or browser
            permission. Users may deny or revoke a permission, but the
            related feature may then be unavailable.
          </p>
          <p style={styles.p}>
            Users may not use Ava Vision, microphone recording, or any
            AVAAIO feature for covert surveillance, unlawful
            recording, stalking, harassment, biometric identification,
            workplace monitoring without authorization, or any other
            purpose that violates privacy, consent, property, or
            recording laws.
          </p>
          <p style={styles.p}>
            Device Motion is an avatar-interaction feature. It is not a
            navigation instrument, medical device, safety sensor,
            location tracker, or reliable measurement of physical
            movement.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>AI Memory and User Control</h2>
          <p style={styles.p}>
            Ava may store conversation history, profile facts,
            semantic memories, protected memories, and other context
            when memory features are active or when the user asks Ava
            to remember information.
          </p>
          <p style={styles.p}>
            Users are responsible for reviewing saved information,
            correcting errors, protecting any memory code, and avoiding
            submission of unnecessary sensitive information. Memory
            retrieval may be incomplete, inaccurate, delayed, or
            associated with the wrong conversational context.
          </p>
          <p style={styles.p}>
            Clearing one category of memory may not automatically
            remove every operational log, conversation record,
            protected-memory entry, billing record, backup, security
            record, or legally retained record.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Ratings and Reviews</h2>
          <p style={styles.p}>
            Ratings and reviews are voluntary. Users must provide an
            honest opinion based on their actual experience and may
            not submit fraudulent, manipulated, automated, purchased,
            threatening, or misleading feedback.
          </p>
          <p style={styles.p}>
            AVAAIO access, preview access, subscriptions, support, and
            account status are not conditioned on providing a positive
            rating or review. Aggregate rating statistics may be shown
            publicly without displaying the verified account’s email.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Acceptable Use</h2>
          <ul style={styles.list}>
            <li>
              No illegal, abusive, harassing, threatening, deceptive,
              exploitative, discriminatory, or harmful activity.
            </li>
            <li>
              No attempts to bypass authentication, subscriptions,
              feature entitlements, administrator controls, rate
              limits, safety systems, or account restrictions.
            </li>
            <li>
              No reverse engineering, unauthorized scraping, account
              resale, credential theft, malware, denial-of-service
              activity, prompt attacks intended to expose protected
              systems, or unauthorized access attempts.
            </li>
            <li>
              No impersonation, fraud, deceptive identity claims,
              intellectual-property infringement, or submission of
              content the user does not have permission to process.
            </li>
            <li>
              No use of AI output, vision results, voice transcripts,
              or motion responses for emergency, medical, legal,
              financial, employment, insurance, law-enforcement,
              security, or other high-impact decisions without
              qualified human review.
            </li>
            <li>
              No attempt by a regular user to access development,
              diagnostic, testing, repair, administrator, or Super
              Administrator tools.
            </li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>User Content</h2>
          <p style={styles.p}>
            Users retain their rights in content they lawfully submit.
            By submitting content, the user grants American Globalo,
            LLC and its service providers a limited right to host,
            transmit, process, reproduce, analyze, and format that
            content only as reasonably necessary to operate, secure,
            support, and improve the requested AVAAIO features.
          </p>
          <p style={styles.p}>
            The user represents that submitted content is lawful and
            that the user has all permissions required to submit,
            record, process, and share it.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Ownership and Intellectual Property</h2>
          <p style={styles.p}>
            AVAAIO™, Ava AI, American Globalo branding, interface
            designs, custom logic, three-dimensional visuals, voice
            experiences, AI workflows, routing systems, memory
            systems, diagnostics, code, animations, copy, and
            proprietary architecture are owned by American Globalo,
            LLC or its licensors unless otherwise stated.
          </p>
          <p style={styles.p}>
            These Terms grant a limited, personal, revocable,
            non-exclusive, non-transferable right to use the service.
            They do not transfer ownership of AVAAIO technology,
            trademarks, source code, models, designs, or confidential
            systems.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>AI Output and User Responsibility</h2>
          <p style={styles.p}>
            Ava produces AI-assisted content that may be inaccurate,
            incomplete, outdated, biased, misleading, offensive,
            inconsistent, or unsuitable. Vision detections, speech
            transcripts, memory recall, translations, calculations,
            and external-information responses may contain errors.
          </p>
          <p style={styles.p}>
            Users must independently evaluate and verify important
            output. AVAAIO does not provide licensed medical, mental
            health, legal, financial, tax, emergency, safety-critical,
            or other professional advice.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Third-Party Services</h2>
          <p style={styles.p}>
            AVAAIO relies on third-party infrastructure and providers,
            which may include AI, speech, cloud, authentication,
            payment, email, app-distribution, content-delivery, and
            security services. Third-party services have their own
            terms, availability, limitations, and privacy practices.
          </p>
          <p style={styles.p}>
            American Globalo, LLC is not responsible for a
            third-party provider’s independent service interruption,
            policy change, account action, payment decision, or
            security incident, except to the extent responsibility
            cannot legally be excluded.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Beta Features and Availability</h2>
          <p style={styles.p}>
            AVAAIO is evolving and may include beta, preview,
            experimental, incomplete, offline, locally processed, or
            provider-dependent features. Features may be changed,
            limited, repaired, suspended, replaced, or removed without
            guaranteeing uninterrupted availability.
          </p>
          <p style={styles.p}>
            Internet outages, provider outages, device limitations,
            permission settings, unsupported browsers, usage limits,
            security controls, and maintenance may reduce or prevent
            access.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Suspension, Deactivation, and Termination</h2>
          <p style={styles.p}>
            American Globalo, LLC may restrict, suspend, deactivate, or
            terminate an account for violations of these Terms,
            unlawful activity, abuse, fraud, payment failure, security
            risk, unauthorized access, or conduct that threatens the
            platform or another person.
          </p>
          <p style={styles.p}>
            A user-requested deletion may begin with account
            deactivation and a limited recovery period, currently up
            to 30 days. Certain records may be retained when reasonably
            necessary for billing, security, fraud prevention, legal
            compliance, dispute resolution, or enforcement.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Disclaimers</h2>
          <p style={styles.p}>
            To the maximum extent permitted by law, AVAAIO is provided
            on an “as is” and “as available” basis. American Globalo,
            LLC does not warrant that the service will be uninterrupted,
            error-free, completely secure, or suitable for every
            purpose, or that AI output will be accurate.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Limitation of Liability</h2>
          <p style={styles.p}>
            To the maximum extent permitted by law, American Globalo,
            LLC and its owners, employees, contractors, affiliates,
            licensors, and providers will not be liable for indirect,
            incidental, special, consequential, exemplary, or punitive
            damages, including lost profits, loss of data, business
            interruption, reliance on AI output, incorrect vision or
            voice results, or unauthorized misuse of the service.
          </p>
          <p style={styles.p}>
            Nothing in these Terms excludes liability that cannot
            legally be excluded or limits rights that applicable
            consumer law does not permit a business to waive.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Governing Law</h2>
          <p style={styles.p}>
            These Terms are governed by the laws of the State of New
            York and applicable United States law, without regard to
            conflict-of-law principles, except where a user’s local
            law requires otherwise.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Changes to These Terms</h2>
          <p style={styles.p}>
            American Globalo, LLC may update these Terms when AVAAIO
            changes, adds features, modifies billing, changes
            providers, or responds to legal requirements. Continued
            use after an updated effective date constitutes acceptance
            where permitted by law.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>Contact</h2>
          <p style={styles.p}>
            Questions about these Terms may be sent to
            support@avaaio.com.
          </p>
        </section>

          <LegalBackToLogin />
        <nav style={styles.nav} aria-label="Legal navigation">
          <Link style={styles.link} to="/privacy-policy">
            Privacy Policy
          </Link>
          <Link style={styles.link} to="/terms-and-conditions">
            Terms & Conditions
          </Link>
          <Link style={styles.link} to="/ai-disclaimer">
            AI Disclaimer
          </Link>
          <Link style={styles.link} to="/credits">
            Credits & Acknowledgments
          </Link>
        </nav>

        <footer style={styles.footer}>
          © 2026 American Globalo, LLC. All rights reserved.
          <br />
          AVAAIO™ is owned and operated by American Globalo, LLC.
          Ava is the AI assistant experience within the AVAAIO™
          platform.
          <br />
          Support: support@avaaio.com
        </footer>
      </article>
    </main>
  );
}
